import { useCallback, useEffect, useRef, useState } from "react";
import { base64ToArrayBuffer, convertPCM16ToFloat32 } from "../utils/audio.js";
const FADE_DURATION_MS = 5;
const applyBoundaryFade = (samples, sampleRate)=>{
    if (!samples.length || !sampleRate) return;
    const fadeSampleCount = Math.min(Math.floor(samples.length / 2), Math.max(1, Math.floor(sampleRate * FADE_DURATION_MS / 1000)));
    if (!fadeSampleCount) return;
    for(let i = 0; i < fadeSampleCount; i++){
        const fadeInGain = i / fadeSampleCount;
        samples[i] *= fadeInGain;
        const tailIndex = samples.length - fadeSampleCount + i;
        if (tailIndex >= 0 && tailIndex < samples.length) {
            const fadeOutGain = Math.max(0, (fadeSampleCount - i - 1) / fadeSampleCount);
            samples[tailIndex] *= fadeOutGain;
        }
    }
};
function useRealTimeAudioPlayer(analytics) {
    const [isPlaying, setIsPlaying] = useState(false);
    const audioContextRef = useRef(null);
    const audioQueueRef = useRef([]);
    const currentSourceRef = useRef(null);
    const isProcessingQueueRef = useRef(false);
    const ensureAudioContext = useCallback(async ()=>{
        if (!audioContextRef.current) audioContextRef.current = new AudioContext();
        if ('suspended' === audioContextRef.current.state) await audioContextRef.current.resume();
        return audioContextRef.current;
    }, []);
    const isInterrupted = useCallback(()=>'yes' === localStorage.getItem('isAudioInterrupted'), []);
    const handleInterruption = useCallback(async ()=>{
        if (isInterrupted()) {
            if (currentSourceRef.current) {
                currentSourceRef.current.stop();
                currentSourceRef.current.disconnect();
                currentSourceRef.current = null;
            }
            audioQueueRef.current = [];
            isProcessingQueueRef.current = false;
            setIsPlaying(false);
        }
    }, [
        isInterrupted
    ]);
    const processAudioChunk = useCallback(async ({ base64AudioData, sampleRate })=>{
        const audioContext = await ensureAudioContext();
        const arrayBuffer = base64ToArrayBuffer(base64AudioData);
        const pcm16Array = new Int16Array(arrayBuffer);
        const float32Array = convertPCM16ToFloat32(pcm16Array);
        if (!float32Array || float32Array.some(isNaN)) throw new Error('Invalid audio data.');
        const buffer = audioContext.createBuffer(1, float32Array.length, sampleRate);
        const audioData = new Float32Array(float32Array);
        applyBoundaryFade(audioData, sampleRate);
        buffer.copyToChannel(audioData, 0);
        return buffer;
    }, [
        ensureAudioContext
    ]);
    const playNextInQueue = useCallback(async ()=>{
        if (isProcessingQueueRef.current || 0 === audioQueueRef.current.length) return;
        isProcessingQueueRef.current = true;
        try {
            if (isInterrupted()) return void await handleInterruption();
            const nextItem = audioQueueRef.current.shift();
            if (!nextItem) {
                isProcessingQueueRef.current = false;
                setIsPlaying(false);
                return;
            }
            const buffer = await processAudioChunk(nextItem);
            const audioContext = await ensureAudioContext();
            if (currentSourceRef.current) {
                currentSourceRef.current.stop();
                currentSourceRef.current.disconnect();
            }
            const source = audioContext.createBufferSource();
            source.buffer = buffer;
            source.connect(audioContext.destination);
            currentSourceRef.current = source;
            source.onended = ()=>{
                currentSourceRef.current = null;
                isProcessingQueueRef.current = false;
                if (audioQueueRef.current.length > 0 && !isInterrupted()) playNextInQueue().catch((error)=>analytics.logError({
                        loggedFrom: 'useRealTimeAudioPlayer.playNextInQueue',
                        message: 'Error playing next audio chunk.',
                        error
                    }));
                else setIsPlaying(false);
            };
            source.start();
            setIsPlaying(true);
        } catch (error) {
            analytics.logError({
                loggedFrom: 'useRealTimeAudioPlayer.playNextInQueue',
                message: 'Failed to play audio chunk.',
                error
            });
            isProcessingQueueRef.current = false;
            if (audioQueueRef.current.length > 0 && !isInterrupted()) playNextInQueue().catch(console.error);
        }
    }, [
        processAudioChunk,
        ensureAudioContext,
        handleInterruption,
        isInterrupted,
        analytics
    ]);
    const playAudio = useCallback(async (base64AudioData, sampleRate = 24000)=>{
        if (!base64AudioData) return;
        audioQueueRef.current.push({
            base64AudioData,
            sampleRate
        });
        if (!isPlaying && !isProcessingQueueRef.current && !isInterrupted()) await playNextInQueue();
    }, [
        isPlaying,
        playNextInQueue,
        isInterrupted
    ]);
    const stopPlayback = useCallback(async ()=>{
        if (currentSourceRef.current) {
            currentSourceRef.current.stop();
            currentSourceRef.current.disconnect();
            currentSourceRef.current = null;
        }
        audioQueueRef.current = [];
        isProcessingQueueRef.current = false;
        setIsPlaying(false);
    }, []);
    useEffect(()=>{
        const onStorageChange = (event)=>{
            if ('isAudioInterrupted' === event.key) handleInterruption().catch(console.error);
        };
        window.addEventListener('storage', onStorageChange);
        const interval = setInterval(()=>handleInterruption().catch(console.error), 300);
        return ()=>{
            window.removeEventListener('storage', onStorageChange);
            clearInterval(interval);
            if (audioContextRef.current) {
                audioContextRef.current.close()?.catch(console.error);
                audioContextRef.current = null;
            }
        };
    }, [
        handleInterruption
    ]);
    return {
        isPlaying,
        queueLength: audioQueueRef.current.length,
        playAudio,
        stopPlayback,
        clearQueue: stopPlayback
    };
}
export { useRealTimeAudioPlayer };
